// Data hooks of the shared-space screens: the caller's spaces, one space, the policy PATCH and the
// creation. The generated client carries the calls (cookies, CSRF, problem+json and the step-up
// replay are the fetcher's); the hooks only keep the caches in step.
import {
  getMySpacesControllerListQueryKey,
  getSpacesControllerGetQueryKey,
  useMySpacesControllerList,
  useOrgSpacesControllerCreate,
  useSpacesControllerGet,
  useSpacesControllerUpdate,
} from '@batiflow/api-client';
import { useSession } from '@batiflow/client-core/react';
import { useQueryClient } from '@tanstack/react-query';
import type { Space } from './model';

/** The spaces the caller belongs to: its organisations' own and other organisations' (external). */
export const useMySpaces = useMySpacesControllerList;

/** One space with the caller's access, tabs and rights. */
export function useSpace(spaceId: string) {
  return useSpacesControllerGet(spaceId);
}

/** PATCH of the name and policy: the space in the cache is replaced by the server's answer. */
export function useUpdateSpace(spaceId: string) {
  const queryClient = useQueryClient();
  return useSpacesControllerUpdate({
    mutation: {
      onSuccess: (space) => {
        queryClient.setQueryData(getSpacesControllerGetQueryKey(spaceId), space);
        void queryClient.invalidateQueries({ queryKey: getMySpacesControllerListQueryKey() });
      },
    },
  });
}

/** POST of a shared space or working group; the lists of spaces are refreshed. */
export function useCreateSpace() {
  const queryClient = useQueryClient();
  return useOrgSpacesControllerCreate({
    mutation: {
      onSuccess: (space) => {
        queryClient.setQueryData(getSpacesControllerGetQueryKey(space.id), space);
        void queryClient.invalidateQueries({ queryKey: getMySpacesControllerListQueryKey() });
      },
    },
  });
}

/**
 * The organisation the reader acts in for this space: the host when the reader is one of its
 * members, else null. Null means another organisation's space (`access.external`): nothing at
 * the organisation level (`/orgs/{orgId}/…`) may be read there.
 */
export function useReaderOrgId(space: Pick<Space, 'host' | 'access'> | undefined): string | null {
  const memberships = useSession((state) => state.me?.memberships);
  if (!space || space.access.external) return null;
  const hostId = space.host.organizationId;
  return memberships?.some((m) => m.organizationId === hostId) ? hostId : null;
}
